import { useEffect } from "react";
import { RouterProvider } from "react-router-dom";
import { router } from "@/router";
import { Toaster } from "@/components/ui";
import { useAppStore, useConversationsStore, useDriveStore, useOllamaStore, useThemeStore } from "@/stores";
import { applyTheme } from "@/theme";
import { logger } from "@/services/logger";
import { initConfigSync } from "@/services/storage/configSync";
import { scheduleAutoBackup } from "@/services/backup";
import { isTauri } from "@/utils";

/**
 * App
 *
 * Composition root. Runs the startup sequence once, mounts the router and the
 * global toast viewport. Deliberately thin — all real structure lives in the
 * layout and pages.
 */
export function App() {
  const theme = useThemeStore((s) => s.theme);
  const setReady = useAppStore((s) => s.setReady);

  useEffect(() => {
    const log = logger.scope("startup");
    log.info("Seufic AI starting");

    // Ensure the persisted theme is on the DOM even before first rehydrate.
    applyTheme(theme);
    log.debug(`Theme applied: ${theme}`);

    // The drive must finish resolving (which root — drive or local fallback
    // — storage lives under) before conversations are loaded against it.
    // Falls through to the same startup sequence even if drive init throws
    // unexpectedly, so a native-layer hiccup never leaves the app stuck
    // before the chat UI can load.
    void useDriveStore
      .getState()
      .init()
      .catch((err: unknown) => log.error("Drive initialization failed; continuing with local storage.", err))
      .finally(() => {
        void useConversationsStore.getState().init();
        initConfigSync();
        if (isTauri()) void scheduleAutoBackup();
      });
    void useOllamaStore.getState().check();

    setReady(true);
    log.info("Shell ready");
    // Runs once on mount; theme changes are handled by the theme store.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <RouterProvider router={router} />
      <Toaster />
    </>
  );
}
